// components/TrainerMoveStrip.js
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { View, Text, StyleSheet, Pressable, ScrollView, Platform } from 'react-native';
import { COLORS, RADIUS, SHADOWS } from '../theme';

const tokenizeMoves = (history) => {
  const tokens = [];
  for (let i = 0; i < history.length; i++) {
    const moveNumber = Math.floor(i / 2) + 1;
    const isWhite = i % 2 === 0;
    tokens.push({
      key: String(i),
      index: i,
      label: isWhite ? `${moveNumber}. ${history[i]}` : history[i],
    });
  }
  return tokens;
};

export default function TrainerMoveStrip({ history, currentMoveIndex, onSelectMove, height = 34 }) {
  const scrollRef = useRef(null);
  const tokenLayoutRef = useRef(new Map());
  const pendingScrollIndexRef = useRef(null);
  const [containerW, setContainerW] = useState(0);

  const tokens = useMemo(() => tokenizeMoves(history), [history]);

  const activeIndex = useMemo(() => {
    if (!Array.isArray(history) || history.length === 0) return -1;
    return Math.max(0, Math.min(history.length - 1, (currentMoveIndex || 0) - 1));
  }, [history, currentMoveIndex]);

  const scrollToToken = useCallback(
    (index, animated = true) => {
      if (!scrollRef.current) return false;
      if (index < 0 || tokens.length === 0) return false;

      const layout = tokenLayoutRef.current.get(String(index));
      if (!layout || containerW <= 0) return false;

      const targetX = Math.max(0, layout.x + layout.width / 2 - containerW / 2);
      scrollRef.current.scrollTo({ x: targetX, animated });
      return true;
    },
    [containerW, tokens.length]
  );

  // Ensure the selected move is always brought into view.
  useEffect(() => {
    if (activeIndex < 0 || tokens.length === 0) return;

    pendingScrollIndexRef.current = activeIndex;

    const didScroll = scrollToToken(activeIndex, true);
    if (didScroll) {
      pendingScrollIndexRef.current = null;
      return;
    }

    // Fallback: if the selected move is the last one, try scrollToEnd while we wait for layout.
    if (activeIndex === tokens.length - 1 && scrollRef.current?.scrollToEnd) {
      scrollRef.current.scrollToEnd({ animated: true });
    }
  }, [activeIndex, tokens.length, scrollToToken]);

  return (
    <View
      style={[styles.wrap, { height }]}
      onLayout={(e) => setContainerW(e?.nativeEvent?.layout?.width || 0)}
    >
      <ScrollView
        ref={scrollRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.row}
        style={styles.scrollView}
      >
        {tokens.length === 0 ? (
          <Text style={styles.placeholder}> </Text>
        ) : (
          tokens.map((t) => {
            const isActive = t.index === activeIndex;
            return (
              <Pressable
                key={t.key}
                onPress={() => onSelectMove && onSelectMove(t.index)}
                onLayout={(e) => {
                  const { x, width } = e?.nativeEvent?.layout || { x: 0, width: 0 };
                  tokenLayoutRef.current.set(String(t.index), { x, width });

                  // If this is the selected move and we were waiting for its layout, scroll now.
                  if (pendingScrollIndexRef.current === t.index) {
                    const didScroll = scrollToToken(t.index, true);
                    if (didScroll) pendingScrollIndexRef.current = null;
                  }
                }}
                style={({ pressed, hovered }) => [
                  styles.token,
                  isActive && styles.tokenActive,
                  (pressed || hovered) && styles.tokenHover,
                ]}
                accessibilityRole="button"
                accessibilityLabel={`Move ${t.label}`}
              >
                <Text style={[styles.tokenText, isActive && styles.tokenTextActive]} numberOfLines={1}>
                  {t.label}
                </Text>
              </Pressable>
            );
          })
        )}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    borderRadius: RADIUS.md,
    backgroundColor: COLORS.trainerMoveStripBg,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    overflow: 'hidden',
    ...SHADOWS.trainerStrip,
    ...Platform.select({
      web: { backdropFilter: 'blur(10px)' },
      default: {},
    }),
  },
  scrollView: {
    ...Platform.select({
      web: {
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      },
      default: {},
    }),
  },
  row: {
    alignItems: 'center',
    paddingHorizontal: 10,
    height: '100%',
  },
  placeholder: {
    color: 'rgba(255,255,255,0.55)',
  },
  token: {
    height: 24,
    paddingHorizontal: 10,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8,
    backgroundColor: 'transparent',
  },
  tokenHover: {
    opacity: 0.9,
  },
  tokenActive: {
    backgroundColor: COLORS.trainerMoveActiveBg,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.22)',
  },
  tokenText: {
    color: 'rgba(255,255,255,0.82)',
    fontSize: 13,
    fontWeight: '700',
    letterSpacing: 0.1,
  },
  tokenTextActive: {
    color: 'rgba(16,16,16,0.92)',
    fontWeight: '900',
  },
});
